<template>
  <form>
    <v-layout pl-4 pb-2>
      <v-flex xs12>
        <blockquote>
          Please carefully choose best-fitting application field, industry and
          vertical market application for your order from the below drop-down
          lists.
        </blockquote>
      </v-flex>
    </v-layout>

    <v-layout row wrap justify-space-between pl-4 pt-3>
      <v-flex xs12 sm6 md4 pt-3 pr-5>
        <auto-select
          v-model="applicationField"
          v-validate="'required'"
          label="Business Line"
          :items="businessLinesList"
          item-text="name"
          return-object
        ></auto-select>
      </v-flex>
      <v-flex xs12 sm6 md4 pt-3 pr-5 v-if="isIndustryRequired">
        <auto-select
          v-model="industry"
          v-validate="'required'"
          label="Market Segment"
          :items="marketSegmentsList"
          item-text="name"
          item-value="value"
        ></auto-select>
      </v-flex>
      <v-flex xs12 sm6 md4 pt-3 pr-5 v-if="isIndustryRequired">
        <auto-select
          v-model="vmApplication"
          v-validate="'required'"
          label="Application"
          :items="applicationsList"
          item-text="name"
          item-value="value"
        ></auto-select>
      </v-flex>
    </v-layout>
    <v-layout row wrap justify-space-between pl-4 pb-3>
      <v-flex xs12 sm6 md4 v-if="isCustomerOptionalOemEnabled">
        <v-checkbox
          v-model="isOptionalOemCheckboxChecked"
          label="OEM order"
          hide-details
          data-vv-name="isOptionalOemCheckboxChecked"
          data-vv-as="OEM order"
          @change="onClickReset"
        ></v-checkbox>
      </v-flex>
    </v-layout>

    <v-layout v-if="isWithEndCustomer" pl-4 pb-2>
      <v-flex xs12>
        <blockquote>
          Please provide below the address of the customer on whose behalf you
          place the order.
          <br />
          You can enter a new address or reuse an existing address from previous
          orders.
        </blockquote>
      </v-flex>
    </v-layout>

    <v-layout v-if="isWithEndCustomer" pb-4>
      <address-list
        collection="endCustomers"
        :items="endCustomers"
        :selected-id="endCustomerId"
        @select="setEndCustomer"
        @onSuccessAddAddress="onAddEndCustomerSuccess"
        selectable
        editable
      ></address-list>
    </v-layout>
    <v-layout row justify-space-between pl-3 pr-3>
      <v-btn class="mr-0" outline color="grey" dark @click="$emit('back')">
        <v-icon small class="pr-2">chevron_left</v-icon>
        Back
      </v-btn>
      <v-btn
        color="primary mx-0"
        :disabled="!endCustomerId && isWithEndCustomer"
        @click="onClickContinue"
      >
        Continue
        <v-icon small class="pl-2">chevron_right</v-icon>
      </v-btn>
    </v-layout>
  </form>
</template>

<script>
import { mapGetters } from "vuex";
import AutoSelect from "@/components/AutoSelect.vue";
import AddressList from "@/components/AddressList.vue";
import { orderSegmentationFilter } from "@/utils/orderSegmentationFilter.js";

export default {
  mounted() {
    this.$store.dispatch("customer/fetchBusinessLinesList");
  },
  $_veeValidate: {
    validator: "new"
  },
  components: {
    AutoSelect,
    AddressList
  },
  data() {
    return {
      modal: false,
      selectedBusinessLineId: undefined,
      selectedMarketSegmentId: undefined,
      isOptionalOemCheckboxChecked: false
    };
  },
  computed: {
    ...mapGetters("basket", {
      endCustomerId: "getEndCustomerId"
    }),
    ...mapGetters("customer", {
      applicationFields: "getApplicationFields",
      industries: "getIndustries",
      endCustomers: "getEndCustomers",
      isIndustryRequired: "isIndustryRequired",
      isWithEndCustomer: "isWithEndCustomer",
      allBusinessLinesList: "getAllBusinessLinesList"
    }),
    isCustomerOptionalOemEnabled: {
      get() {
        return !!this.$store.state.customer.isOptionalOemEnabled;
      }
    },
    isCustomerOemEnabled: {
      get() {
        const hasOemCustomerConfig =
          this.$store.state.customer.customizationId === "AMAT";
        const hasOemCustomerFlag = this.isCustomerOptionalOemEnabled;

        const canUserSelectOem = hasOemCustomerConfig || hasOemCustomerFlag;

        const isOemEnabled =
          (canUserSelectOem && this.isOptionalOemCheckboxChecked) ||
          hasOemCustomerConfig;

        return isOemEnabled;
      }
    },
    applicationField: {
      get() {
        return this.$store.state.basket.applicationField;
      },
      set({ value, id }) {
        this.onBusinessLineSelected(id);
        this.selectedMarketSegmentId = undefined;
        this.$store.dispatch("basket/setApplicationField", value);
      }
    },
    industry: {
      get() {
        return this.$store.state.basket.industry;
      },
      set({ value, id }) {
        this.onMarketSegmentSelected(id);
        this.$store.dispatch("basket/setIndustry", value);
      }
    },
    vmApplication: {
      get() {
        return this.$store.state.basket.vmApplication;
      },
      set({ value }) {
        this.$store.dispatch("basket/setVmApplication", value);
      }
    },
    businessLinesList() {
      return this.allBusinessLinesList.filter(
        orderSegmentationFilter({
          isOem: this.isCustomerOemEnabled,
          parentId: undefined,
          type: "businessLine"
        })
      );
    },
    marketSegmentsList() {
      return this.allBusinessLinesList.filter(
        orderSegmentationFilter({
          isOem: this.isCustomerOemEnabled,
          parentId: this.selectedBusinessLineId,
          type: "marketSegment"
        })
      );
    },
    applicationsList() {
      return this.allBusinessLinesList.filter(
        orderSegmentationFilter({
          isOem: this.isCustomerOemEnabled,
          parentId: this.selectedMarketSegmentId,
          type: "application"
        })
      );
    }
  },
  methods: {
    setEndCustomer(value) {
      if (value.id && value.id == this.endCustomerId) {
        this.$store.dispatch("basket/unsetEndCustomer");
      } else {
        this.$store.dispatch("basket/setEndCustomer", value);
      }
    },
    onClickContinue() {
      this.$validator.validate().then(valid => {
        if (!valid) {
          return;
        }
        this.$emit("continue");
      });
    },
    onClickReset() {
      this.$validator.reset();

      this.$nextTick(() => {
        this.$store.dispatch("basket/setVmApplication", null);
        this.$store.dispatch("basket/setIndustry", null);
        this.$store.dispatch("basket/setApplicationField", null);
        this.selectedBusinessLineId = undefined;
        this.selectedMarketSegmentId = undefined;
      });
    },
    onAddEndCustomerSuccess(endCustomer) {
      this.$store.dispatch("basket/setEndCustomer", endCustomer);
    },
    onBusinessLineSelected(selectedBusinessLineId) {
      this.selectedBusinessLineId = selectedBusinessLineId;
    },
    onMarketSegmentSelected(selectedMarketSegmentId) {
      this.selectedMarketSegmentId = selectedMarketSegmentId;
    }
  },
  watch: {
    applicationField() {
      this.$store.dispatch("basket/setIndustry", null);
      this.$store.dispatch("basket/setVmApplication", null);
    },
    industry() {
      this.$store.dispatch("basket/setVmApplication", null);
    }
  }
};
</script>
